import type { InputHTMLAttributes, ReactNode } from 'react'

import { forwardRef } from 'react'
import { cn } from '@/lib/utils'

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
    label?: ReactNode
    error?: string
    hint?: ReactNode
}

const inputBase = 'block w-full rounded-card border border-transparent bg-white/80 px-4 py-3 text-base text-neutral-900 placeholder:text-neutral-400 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.04)] focus:border-brand-red/50 focus:bg-white focus:outline-none focus:ring-4 focus:ring-brand-red/15 transition-colors'

const Field = forwardRef<HTMLInputElement, FieldProps>(({ className, label, error, hint, id, ...props }, ref) => {
    const inputId = id ?? props.name

    return (
        <div className='flex flex-col gap-1.5'>
            {label ? (
                <label htmlFor={inputId} className='text-sm font-medium text-neutral-700'>
                    {label}
                </label>
            ) : null}

            <input
                ref={ref}
                id={inputId}
                aria-invalid={Boolean(error) || undefined}
                aria-describedby={error ? `${inputId}-error` : undefined}
                className={cn(inputBase, error && 'ring-2 ring-brand-red/40 focus:ring-brand-red/30', className)}
                {...props}
            />

            {hint && !error ? <p className='text-xs text-neutral-500'>{hint}</p> : null}

            {error ? (
                <p id={`${inputId}-error`} className='text-xs font-medium text-brand-red'>
                    {error}
                </p>
            ) : null}
        </div>
    )
})

Field.displayName = 'Field'

export default Field
